import { ReusableWizard } from "@oysterhr/feature-components"
import { Button, LoadingSpinner, Text, Toast, useToast } from "@oysterhr/ui"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { useNavigate } from "react-router-dom"
import { PaymentMethodStripeSepa } from "@oysterhr/schema"
import { useTranslation } from "react-i18next"
import { useAuthorizePaymentMethod } from "../../../../hooks/useAuthorizeDirectDebitOnPaymentMethod"
import { useNewCompanyPaymentMethods } from "../../../../hooks/useNewCompanyPaymentMethods"
import { formatAddressToSingleLine } from "../../../../utils"
import { companyPaymentMethodsPath } from "../../../../../../utils/routeHelpers"

const FieldCell = ({ name, value }: { name: string; value?: string | null }) => {
  if (!value) return null

  return (
    <div className="flex flex-col">
      <Text className="font-semibold">{name}</Text>
      <Text>{value}</Text>
    </div>
  )
}

export const AuthorizeDebitStep = () => {
  // Hooks
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/SepaFlow", {
    keyPrefix: "authorizationStep",
  })
  const { accountAspect: companyId } = useAccountAspect()
  const navigate = useNavigate()
  const toast = useToast()

  // GraphQL
  const [authorize] = useAuthorizePaymentMethod()
  const { data } = useNewCompanyPaymentMethods({
    variables: { companyId },
    fetchPolicy: "network-only",
  })

  // Derived state
  const paymentMethod = data?.company?.paymentMethod as PaymentMethodStripeSepa | undefined

  // Actions
  const onAuthorize = () => {
    const paymentMethodId = paymentMethod?.id

    authorize({
      variables: { authorized: true, paymentMethodId: paymentMethodId ?? "" },
      onCompleted: () => {
        toast({
          render: (
            <Toast variation="success">
              <Toast.Title>{t("toast.title")}</Toast.Title>
              <Toast.Content>{t("toast.content")}</Toast.Content>
            </Toast>
          ),
        })
        navigate(companyPaymentMethodsPath(companyId))
      },
    })
  }

  return (
    <ReusableWizard.Step title={t("title")}>
      <div className="space-y-4 text-left">
        <Text className="mt-8">{t("contractorContent")}</Text>
        {paymentMethod ? (
          <div>
            <Text className="mb-4 mt-4 text-lg font-normal leading-6">
              {t("accountToBeDebited")}
            </Text>
            <div className="flex flex-col gap-4">
              <FieldCell name={t("accountHolderName")} value={paymentMethod.accountHolderName} />
              <FieldCell
                name={t("accountAddress")}
                value={formatAddressToSingleLine(paymentMethod.billingAddress)}
              />
              <FieldCell
                name={t("accountIBAN")}
                value={`****${paymentMethod.accountNumber.slice(-4)}`}
              />
            </div>
          </div>
        ) : (
          <LoadingSpinner />
        )}
        <div className="mt-8 flex space-x-8">
          <Button onClick={onAuthorize}>{t("btnAuthorize")}</Button>
        </div>
      </div>
    </ReusableWizard.Step>
  )
}
